import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { LuMessageCircle } from "react-icons/lu";
import { HiOutlineXMark } from "react-icons/hi2";
import { aiTenant } from "../../config/config";

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, sender: "bot", text: "Hi! How can I help you today?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const messagesEndRef = useRef(null);

  const suggestions = [
    "How much did I earn today?",
    "What was my revenue yesterday compared to today?",
    "What’s the total sales in the last 7 days?",
    "Compare last 30 days with the previous 30 days revenue.",
    "What are my best-selling items in the last month?",
    "Which categories bring the most revenue?",
    "Which items are not selling at all?",
    "How many orders did I get today?",
    "What’s the average order value this week?",
    "Show me the details of my last order.",
  ];

  const toggleChat = () => setIsOpen(!isOpen);

  const sendMessage = async (e, textOverride = null) => {
    e?.preventDefault();
    const messageText = textOverride || input;
    if (!messageText.trim()) return;

    const userMessage = { id: Date.now(), sender: "user", text: messageText };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setShowSuggestions(false);
    setLoading(true);

    try {
      const res = await axios.post(
        "https://ai.imdadyrms.co/ask",
        {
          question: userMessage.text,
        },
        {
          params: {
            tenant: aiTenant,
          },
        }
      );

      const botMessage = {
        id: Date.now() + 1,
        sender: "bot",
        text: res.data.answer || "Sorry, I couldn't find an answer.",
        tips: res.data.tips,
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 2,
          sender: "bot",
          text: "⚠️ Error connecting to AI.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Auto scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading]);

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="p-4 rounded-full bg-d-cat-btn text-white shadow-lg hover:bg-[#a22fff] transition"
        >
          <LuMessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Box */}
      {isOpen && (
        <div className="w-80 h-96 bg-white rounded-2xl shadow-xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between bg-d-bg text-white px-4 py-3 rounded-t-2xl">
            <h2 className="font-semibold">Chat</h2>
            <button onClick={toggleChat}>
              <HiOutlineXMark className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 p-4 overflow-y-auto space-y-2 text-sm">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${
                  msg.sender === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <span
                  className={`px-3 py-2 rounded-lg max-w-[70%] whitespace-pre-line ${
                    msg.sender === "user"
                      ? "bg-d-bg text-white rounded-br-none"
                      : "bg-gray-200 text-gray-900 rounded-bl-none"
                  }`}
                >
                  {/* Answer */}
                  {msg.text && (
                    <p className="font-semibold text-sm mb-2">{msg.text}</p>
                  )}

                  {/* Tips */}
                  {msg.tips && (
                    <div className="mt-2 p-2 rounded-lg bg-white/70 border border-gray-300 text-gray-800 text-xs leading-relaxed whitespace-pre-line">
                      <h4 className="font-semibold text-d-bg-text mb-1">
                        💡 Owner Tips
                      </h4>
                      {msg.tips}
                    </div>
                  )}
                </span>
              </div>
            ))}
            {loading && (
              <div className="text-gray-500 text-sm italic">
                AI is typing...
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input + Suggestions */}
          <div className="relative">
            <form
              onSubmit={sendMessage}
              className="p-3 border-t flex items-center gap-2"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  setShowSuggestions(false);
                }}
                onFocus={() => setShowSuggestions(true)}
                placeholder="Type a message..."
                className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#7B20C7]"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading}
                className="px-3 py-2 bg-d-bg text-white rounded-lg hover:bg-d-bg-dark disabled:opacity-50"
              >
                Send
              </button>
            </form>

            {/* Suggestions Dropdown */}
            {showSuggestions && !loading && (
              <div className="absolute bottom-14 left-3 right-3 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto text-sm z-50">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(null, s)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
